import React, { useEffect, useState, useCallback } from "react";
import { formatBRL, formatDateTimeBR } from "../lib/api";
import { fetchWithV2Fallback } from "../lib/v2";
import { CreditCard, RefreshCw, AlertCircle } from "lucide-react";
import { Button } from "../components/ui/button";
import { Skeleton } from "../components/ui/skeleton";
import StatusBadge from "../components/StatusBadge";

export default function Payments() {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [source, setSource] = useState("");
  const [error, setError] = useState(null);

  const load = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const result = await fetchWithV2Fallback("/v2/payments", null);
      const list = result.data?.data || result.data?.payments || (Array.isArray(result.data) ? result.data : []);
      setItems(list);
      setSource(result.source);
    } catch (e) {
      setError("Erro ao carregar pagamentos.");
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { load(); }, [load]);

  return (
    <div className="space-y-6" data-testid="payments-page">
      <div className="flex flex-col sm:flex-row sm:items-end sm:justify-between gap-3">
        <div>
          <span className="zemo-overline">Financeiro</span>
          <h1 className="mt-1.5 text-3xl sm:text-[34px] font-semibold tracking-tight text-zinc-900">Pagamentos</h1>
          <p className="mt-1 text-sm text-zinc-500">Histórico de pagamentos e liquidações.</p>
        </div>
        <Button variant="outline" size="sm" onClick={load} disabled={loading} data-testid="payments-refresh">
          <RefreshCw className={`h-3.5 w-3.5 mr-1.5 ${loading ? "animate-spin" : ""}`} /> Atualizar
        </Button>
      </div>

      {error && (
        <div className="flex items-center gap-2 p-4 rounded-lg bg-red-50 border border-red-200 text-red-800 text-sm" data-testid="payments-error">
          <AlertCircle className="h-4 w-4" /> {error}
        </div>
      )}

      {loading ? (
        <Skeleton className="h-64 rounded-lg" />
      ) : items.length === 0 ? (
        <div className="zemo-card p-12 text-center" data-testid="payments-empty">
          <CreditCard className="h-10 w-10 text-zinc-300 mx-auto mb-3" />
          <p className="text-zinc-500">Nenhum pagamento registrado</p>
          <p className="text-xs text-zinc-400 mt-1">Pagamentos aparecerão quando operações forem liquidadas.</p>
        </div>
      ) : (
        <div className="zemo-card overflow-hidden">
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead className="bg-zinc-50/60 text-[11px] uppercase tracking-wider text-zinc-500 border-b border-[var(--zemo-border)]">
                <tr>
                  <th className="text-left px-5 py-3 font-semibold">Referência</th>
                  <th className="text-left px-5 py-3 font-semibold">Cedente</th>
                  <th className="text-right px-5 py-3 font-semibold">Valor</th>
                  <th className="text-left px-5 py-3 font-semibold">Status</th>
                  <th className="text-left px-5 py-3 font-semibold">Data</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-[var(--zemo-border)]" data-testid="payments-table">
                {items.map((p, i) => (
                  <tr key={p.id || i} className="hover:bg-zinc-50/60">
                    <td className="px-5 py-3.5 font-mono-num font-semibold text-zinc-900">{p.reference || p.numero || `PAG-${i + 1}`}</td>
                    <td className="px-5 py-3.5 text-zinc-700">{p.cedente_nome || p.participant_name || "—"}</td>
                    <td className="px-5 py-3.5 text-right font-semibold font-mono-num">{formatBRL(p.amount || p.valor || 0)}</td>
                    <td className="px-5 py-3.5"><StatusBadge status={p.status || "pendente"} /></td>
                    <td className="px-5 py-3.5 text-[12px] text-zinc-500">{formatDateTimeBR(p.paid_at || p.created_at)}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}

      {source === "v2" && (
        <div className="text-[11px] text-zinc-400 text-right">Fonte: API V2</div>
      )}
    </div>
  );
}
